import React, { useState } from "react";
import CommonTableTwoLine from "../../../components/common/common-table/common-table-two-line";
import InventoryComparisonColumnModel from "./inventory-comparison-column-model";
import { ApiRequest } from "../../../services/api/api";
import { DatePicker, Button, Space, Card, Row, Col, Statistic } from "antd";
import moment from "moment";
import { ArrowUpOutlined, ArrowDownOutlined, MinusOutlined } from "@ant-design/icons";

const { RangePicker } = DatePicker;

interface MetricComparison {
  currentMonthValue: number;
  previousMonthValue: number;
  changeAmount: number;
  changePercent: number;
  displayText: string;
}

interface InventoryComparisonData {
  currentMonthDate: string;
  previousMonthDate: string;
  totalValue: MetricComparison;
  totalBoxes: MetricComparison;
  slowMovingValue: MetricComparison;
  deadStockValue: MetricComparison;
  customerSpecificValue: MetricComparison;
  composition?: any[];
}

const InventoryComparisonReportPage: React.FC = () => {
  const [data, setData] = useState<InventoryComparisonData | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<moment.Moment>(moment());

  const fetchComparisonData = async (date: moment.Moment) => {
    setLoading(true);
    try {
      console.log("Fetching inventory comparison data for:", date.format("YYYY-MM-DD"));
      const result = await ApiRequest({
        otherUrl: "SlowMovingStockSnapshot/inventory-comparison",
        method: "get",
        params: { currentMonth: date.format("YYYY-MM-DD") },
        isShowSpinner: true,
      });
      console.log("Inventory comparison result:", result);
      setData(result?.data?.data || null);
    } catch (error: any) {
      console.error("Failed to load inventory comparison data", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchComparisonData(selectedDate);
  }, []);

  const handleDateChange = (date: moment.Moment | null) => {
    if (date) {
      setSelectedDate(date);
      fetchComparisonData(date);
    }
  };

  const renderMetricCard = (title: string, metric: MetricComparison | undefined) => {
    if (!metric) return null;

    const isPositive = metric.changePercent > 0;
    const isNegative = metric.changePercent < 0;
    const isNeutral = metric.changePercent === 0;

    const arrowIcon = isPositive ? (
      <ArrowUpOutlined style={{ color: "#cf1322" }} />
    ) : isNegative ? (
      <ArrowDownOutlined style={{ color: "#389e0d" }} />
    ) : (
      <MinusOutlined style={{ color: "#8c8c8c" }} />
    );

    const changeColor = isPositive ? "#cf1322" : isNegative ? "#389e0d" : "#8c8c8c";

    return (
      <Card bordered={false} style={{ background: "#fafafa" }}>
        <Statistic
          title={title}
          value={metric.currentMonthValue}
          precision={2}
          prefix="$"
          suffix={
            <div style={{ fontSize: 14, marginTop: 8 }}>
              <Space>
                {arrowIcon}
                <span style={{ color: changeColor, fontWeight: "bold" }}>
                  {isPositive ? "+" : ""}{metric.changePercent.toFixed(2)}%
                </span>
                <span style={{ color: "#8c8c8c" }}>
                  ({metric.previousMonthValue.toFixed(2)} → {metric.currentMonthValue.toFixed(2)})
                </span>
              </Space>
            </div>
          }
        />
      </Card>
    );
  };

  const tableData = data ? [data] : [];

  return (
    <div>
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2>Inventory Comparison Report</h2>
        <Space>
          <DatePicker
            picker="month"
            value={selectedDate}
            onChange={handleDateChange}
            format="YYYY-MM"
            placeholder="Select Month"
          />
          <Button
            type="primary"
            onClick={() => fetchComparisonData(selectedDate)}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {data && (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              {renderMetricCard("Total Value", data.totalValue)}
            </Col>
            <Col span={6}>
              {renderMetricCard("Total Boxes", data.totalBoxes)}
            </Col>
            <Col span={6}>
              {renderMetricCard("Slow Moving", data.slowMovingValue)}
            </Col>
            <Col span={6}>
              {renderMetricCard("Dead Stock", data.deadStockValue)}
            </Col>
          </Row>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              {renderMetricCard("Customer Specific", data.customerSpecificValue)}
            </Col>
          </Row>
        </>
      )}

      <CommonTableTwoLine
        title="Inventory Comparison Details"
        column={InventoryComparisonColumnModel()}
        initData={tableData}
        editable={false}
        isNotAddable={true}
        isNotEditable={true}
        isNotDeletable={true}
        isExportable={true}
        defaultPageSize={1}
      />
    </div>
  );
};

export default InventoryComparisonReportPage;
